import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { formatDisplayDateTime, getCurrentAESTDate } from '../utils/dateUtils';
import {
  MapPin,
  Users,
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Plus,
  Search,
  Filter,
  Eye,
  MessageSquare,
  Calendar,
  X,
  Rss,
  Copy,
  Check
} from 'lucide-react';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

interface Worker {
  id: string;
  name: string;
  role: string;
  email: string;
  phone?: string;
  status: string;
}

interface Site {
  id: string;
  name: string;
  address: string;
  type: string;
  status: string;
}

interface OutOfAreaRequest {
  id: string;
  worker_id: string;
  site_id?: string;
  reason: string;
  status: 'pending' | 'approved' | 'denied';
  requested_at: string;
  reviewed_at?: string;
  reviewed_by?: string;
  admin_notes?: string;
  latitude?: number;
  longitude?: number;
  created_at: string;
  updated_at: string;
  worker?: Worker;
  site?: Site;
}

export const OutOfAreaRequestsPage: React.FC = () => {
  const [requests, setRequests] = useState<OutOfAreaRequest[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<OutOfAreaRequest | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [copied, setCopied] = useState(false);

  const rssFeedUrl = `${SUPABASE_URL}/functions/v1/rss-out-of-area`;

  const handleCopyRss = async () => {
    try {
      await navigator.clipboard.writeText(rssFeedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers that block clipboard without user gesture
      prompt('Copy this RSS URL:', rssFeedUrl);
    }
  };

  const [newRequest, setNewRequest] = useState({
    worker_id: '',
    site_id: '',
    reason: '',
    latitude: '',
    longitude: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      await Promise.all([
        fetchRequests(),
        fetchWorkers(),
        fetchSites()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('out_of_area_requests')
        .select(`
          *,
          workers!inner(id, name, role, email, phone, status),
          sites(id, name, address, type, status)
        `)
        .order('requested_at', { ascending: false });

      if (error) throw error;

      const requestsWithDetails: OutOfAreaRequest[] = (data || []).map(request => ({
        ...request,
        worker: request.workers,
        site: request.sites
      }));

      setRequests(requestsWithDetails);
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  const fetchWorkers = async () => {
    try {
      const { data, error } = await supabase
        .from('workers')
        .select('*')
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      setWorkers(data || []);
    } catch (error) {
      console.error('Error fetching workers:', error);
    }
  };

  const fetchSites = async () => {
    try {
      const { data, error } = await supabase
        .from('sites')
        .select('*')
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      setSites(data || []);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const handleSubmitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const requestData = {
        worker_id: newRequest.worker_id,
        site_id: newRequest.site_id || null,
        reason: newRequest.reason,
        latitude: newRequest.latitude ? Number(newRequest.latitude) : null,
        longitude: newRequest.longitude ? Number(newRequest.longitude) : null,
        status: 'pending' as const
      };

      const { error } = await supabase
        .from('out_of_area_requests')
        .insert([requestData]);

      if (error) throw error;

      setShowModal(false);
      setNewRequest({
        worker_id: '',
        site_id: '',
        reason: '',
        latitude: '',
        longitude: ''
      });
      fetchRequests();
    } catch (error) {
      console.error('Error submitting request:', error);
      alert('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReviewRequest = async (requestId: string, newStatus: 'approved' | 'denied') => {
    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('out_of_area_requests')
        .update({
          status: newStatus,
          reviewed_at: new Date().toISOString(),
          reviewed_by: user?.id,
          admin_notes: reviewNotes
        })
        .eq('id', requestId);

      if (error) throw error;

      setShowReviewModal(false);
      setSelectedRequest(null);
      setReviewNotes('');
      fetchRequests();
    } catch (error) {
      console.error('Error reviewing request:', error);
      alert('Failed to review request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'denied':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'denied':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-600" />;
    }
  };

  const filteredRequests = requests.filter(request => {
    const matchesSearch = request.worker?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         request.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         request.site?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || request.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const approvedCount = requests.filter(r => r.status === 'approved').length;
  const deniedCount = requests.filter(r => r.status === 'denied').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Out of Area Requests</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage worker requests to work outside designated site boundaries
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* RSS feed button */}
          <button
            onClick={handleCopyRss}
            title="Copy RSS feed URL"
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
              copied
                ? 'bg-green-50 border-green-200 text-green-700'
                : 'bg-white border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50'
            }`}
          >
            {copied ? <Check className="h-4 w-4" /> : <Rss className="h-4 w-4" />}
            {copied ? 'Copied!' : 'RSS Feed'}
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            <span>New Request</span>
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <MapPin className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Requests</p>
              <p className="text-xl font-bold text-gray-900">{requests.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Review</p>
              <p className="text-xl font-bold text-yellow-600">{pendingCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Approved</p>
              <p className="text-xl font-bold text-green-600">{approvedCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Denied</p>
              <p className="text-xl font-bold text-red-600">{deniedCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by worker name, reason, or site..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="denied">Denied</option>
            </select>
          </div>
        </div>
      </div>

      {/* Requests Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredRequests.map((request) => (
          <div
            key={request.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            {/* Header with Status */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Out of Area Request</h3>
                  <p className="text-sm text-gray-500">
                    {formatDisplayDateTime(request.requested_at)}
                  </p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(request.status)}`}>
                <div className="flex items-center space-x-1">
                  {getStatusIcon(request.status)}
                  <span className="capitalize">{request.status}</span>
                </div>
              </div>
            </div>

            {/* Worker Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex items-center space-x-2 mb-2">
                <Users className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">Worker</span>
              </div>
              <div className="text-sm text-blue-800">
                <p className="font-medium">{request.worker?.name}</p>
                <p className="text-xs capitalize">{request.worker?.role?.replace('_', ' ')}</p>
                <p className="text-xs">{request.worker?.email}</p>
              </div>
            </div>

            {/* Site Information (if applicable) */}
            {request.site && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Building2 className="h-4 w-4 text-orange-600" />
                  <span className="text-sm font-medium text-orange-900">Related Site</span>
                </div>
                <div className="text-sm text-orange-800">
                  <p className="font-medium">{request.site.name}</p>
                  <p className="text-xs">{request.site.address}</p>
                </div>
              </div>
            )}

            {/* Request Details */}
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Reason</label>
                <p className="text-sm text-gray-900 bg-gray-50 rounded-md p-2 border">
                  {request.reason}
                </p>
              </div>

              {/* GPS Coordinates */}
              {(request.latitude || request.longitude) && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                  <div className="bg-gray-50 rounded-md p-2 border">
                    <button
                      onClick={() => {
                        if (request.latitude && request.longitude) {
                          const googleMapsUrl = `https://www.google.com/maps?q=${request.latitude},${request.longitude}`;
                          window.open(googleMapsUrl, '_blank');
                        }
                      }}
                      className="flex items-center space-x-2 text-xs text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                      title="Open in Google Maps"
                    >
                      <MapPin className="h-3 w-3" />
                      <span>
                        {request.latitude?.toFixed(6)}, {request.longitude?.toFixed(6)}
                      </span>
                      <span className="text-xs text-blue-500 ml-1">(Click to view on map)</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Review Information */}
              {request.status !== 'pending' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Review Details</label>
                  <div className="bg-gray-50 rounded-md p-2 border">
                    <div className="text-xs text-gray-600 space-y-1">
                      <p>
                        <span className="font-medium">Reviewed:</span> {' '}
                        {request.reviewed_at ? formatDisplayDateTime(request.reviewed_at) : 'N/A'}
                      </p>
                      {request.admin_notes && (
                        <p>
                          <span className="font-medium">Notes:</span> {request.admin_notes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex space-x-2 pt-3 border-t border-gray-200">
              {request.status === 'pending' && (
                <>
                  <button
                    onClick={() => {
                      setSelectedRequest(request);
                      setReviewNotes('');
                      setShowReviewModal(true);
                    }}
                    className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                  >
                    <Eye className="h-4 w-4" />
                    <span>Review</span>
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  setSelectedRequest(request);
                  setShowReviewModal(true);
                }}
                className="flex items-center justify-center px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                title="View Details"
              >
                <Eye className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredRequests.length === 0 && (
        <div className="text-center py-12">
          <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || filterStatus ? 'No matching requests found' : 'No out of area requests'}
          </h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || filterStatus 
              ? 'Try adjusting your search or filter criteria.'
              : 'Workers can submit requests to work outside designated site boundaries.'
            }
          </p>
          {(searchTerm || filterStatus) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('');
              }}
              className="text-orange-600 hover:text-orange-700 text-sm font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Add Request Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">New Out of Area Request</h3>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitRequest} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Users className="inline h-4 w-4 mr-1" />
                    Worker *
                  </label>
                  <select
                    value={newRequest.worker_id}
                    onChange={(e) => setNewRequest(prev => ({ ...prev, worker_id: e.target.value }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">Select a worker</option>
                    {workers.map(worker => (
                      <option key={worker.id} value={worker.id}>
                        {worker.name} - {worker.role.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building2 className="inline h-4 w-4 mr-1" />
                    Related Site (Optional)
                  </label>
                  <select
                    value={newRequest.site_id}
                    onChange={(e) => setNewRequest(prev => ({ ...prev, site_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">No specific site</option>
                    {sites.map(site => (
                      <option key={site.id} value={site.id}>
                        {site.name} - {site.address}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Out of Area Work *
                </label>
                <textarea
                  value={newRequest.reason}
                  onChange={(e) => setNewRequest(prev => ({ ...prev, reason: e.target.value }))}
                  required
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Explain why work needs to be performed outside the designated site boundary..."
                />
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-4 flex items-center">
                  <MapPin className="h-4 w-4 mr-2 text-gray-600" />
                  Work Location (Optional)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Latitude</label>
                    <input
                      type="number"
                      step="0.000001"
                      min="-90"
                      max="90"
                      value={newRequest.latitude}
                      onChange={(e) => setNewRequest(prev => ({ ...prev, latitude: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="-33.8688"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Longitude</label>
                    <input
                      type="number"
                      step="0.000001"
                      min="-180"
                      max="180"
                      value={newRequest.longitude}
                      onChange={(e) => setNewRequest(prev => ({ ...prev, longitude: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="151.2093"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Provide GPS coordinates of the work location if known
                </p>
              </div>

              <div className="flex space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 transition-colors"
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Eye className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">Review Request</h3>
              </div>
              <button
                onClick={() => {
                  setShowReviewModal(false);
                  setSelectedRequest(null);
                  setReviewNotes('');
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Request Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Request Summary</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Worker:</span>
                    <p className="text-gray-900">{selectedRequest.worker?.name}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Role:</span>
                    <p className="text-gray-900 capitalize">{selectedRequest.worker?.role?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Requested:</span>
                    <p className="text-gray-900">{formatDisplayDateTime(selectedRequest.requested_at)}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Status:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedRequest.status)}`}>
                      {selectedRequest.status.charAt(0).toUpperCase() + selectedRequest.status.slice(1)}
                    </span>
                  </div>
                </div>
                
                <div className="mt-4">
                  <span className="font-medium text-gray-700">Reason:</span>
                  <p className="text-gray-900 mt-1 bg-white rounded-md p-3 border">
                    {selectedRequest.reason}
                  </p>
                </div>

                {selectedRequest.site && (
                  <div className="mt-4">
                    <span className="font-medium text-gray-700">Related Site:</span>
                    <p className="text-gray-900">{selectedRequest.site.name} - {selectedRequest.site.address}</p>
                  </div>
                )}

                {(selectedRequest.latitude || selectedRequest.longitude) && (
                  <div className="mt-4">
                    <span className="font-medium text-gray-700">GPS Location:</span>
                    <div className="mt-1">
                      <button
                        onClick={() => {
                          if (selectedRequest.latitude && selectedRequest.longitude) {
                            const googleMapsUrl = `https://www.google.com/maps?q=${selectedRequest.latitude},${selectedRequest.longitude}`;
                            window.open(googleMapsUrl, '_blank');
                          }
                        }}
                        className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors bg-blue-50 hover:bg-blue-100 rounded-md p-2 border border-blue-200"
                        title="Open in Google Maps"
                      >
                        <MapPin className="h-4 w-4" />
                        <span className="font-mono text-sm">
                          {selectedRequest.latitude?.toFixed(6)}, {selectedRequest.longitude?.toFixed(6)}
                        </span>
                        <span className="text-xs text-blue-500">📍 View on Map</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Admin Notes */}
              {selectedRequest.status === 'pending' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MessageSquare className="inline h-4 w-4 mr-1" />
                    Admin Notes (Optional)
                  </label>
                  <textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Add notes about the decision..."
                  />
                </div>
              )}

              {/* Existing Admin Notes */}
              {selectedRequest.admin_notes && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Previous Admin Notes</h4>
                  <p className="text-sm text-blue-800">{selectedRequest.admin_notes}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4 border-t border-gray-200">
                {selectedRequest.status === 'pending' ? (
                  <>
                    <button
                      onClick={() => handleReviewRequest(selectedRequest.id, 'denied')}
                      disabled={submitting}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      {submitting ? 'Processing...' : 'Deny Request'}
                    </button>
                    <button
                      onClick={() => handleReviewRequest(selectedRequest.id, 'approved')}
                      disabled={submitting}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      {submitting ? 'Processing...' : 'Approve Request'}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setShowReviewModal(false);
                      setSelectedRequest(null);
                      setReviewNotes('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};